import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useFeature } from "../context/FeatureContext";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, Plus, Pencil, Trash2, Download, Upload, Search,
  Building2, Store, Users, AlertTriangle, X, FileUp,
  ChevronDown, ChevronRight, Briefcase, Receipt, FolderOpen, SprayCan
} from "lucide-react";
import { Input } from "../components/ui/input";
import DateInput from "../components/DateInput";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import SearchableSelect from "../components/SearchableSelect";
import { cn } from "../lib/utils";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ArchivioDocumenti = () => {
  const { token, localeCorrente } = useAuth();
  const { hasFeature } = useFeature();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("societa");
  const [search, setSearch] = useState("");
  
  // Data
  const [documenti, setDocumenti] = useState([]);
  const [locali, setLocali] = useState([]);
  const [dipendenti, setDipendenti] = useState([]);
  const [stats, setStats] = useState({});
  
  // Filtri
  const [filtroLocale, setFiltroLocale] = useState("all");
  const [filtroDipendente, setFiltroDipendente] = useState("all");
  
  // Personale tab: expanded employees
  const [expandedDip, setExpandedDip] = useState(null);
  const [dipContratti, setDipContratti] = useState({});
  const [dipBuste, setDipBuste] = useState({});
  const hasPersonale = hasFeature("schede_personale");
  
  // Prodotti Pulizia tab
  const [prodottiPulizia, setProdottiPulizia] = useState([]);
  
  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [tipologiaDoc, setTipologiaDoc] = useState("documento"); // documento | contratto | busta_paga
  const [formBusta, setFormBusta] = useState({ anno: new Date().getFullYear(), mese: new Date().getMonth() + 1 });
  const [formContratto, setFormContratto] = useState({ data_inizio: "", data_scadenza: "" });
  const [form, setForm] = useState({
    nome: "",
    data_documento: "",
    data_scadenza: "",
    note: "",
    locale_id: "",
    dipendente_id: "",
    file: null,
    file_name: ""
  });
  const [uploading, setUploading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const getAuthHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`
  }), [token]);

  const fetchData = useCallback(async () => {
    if (!localeCorrente?.id) return;
    try {
      const [docRes, localiRes, dipRes, statsRes, prodPulRes] = await Promise.all([
        axios.get(`${API}/documenti`, { headers: getAuthHeaders() }),
        axios.get(`${API}/locali`, { headers: getAuthHeaders() }),
        axios.get(`${API}/personale/utenti-turni?locale_id=${localeCorrente.id}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/documenti/stats/riepilogo`, { headers: getAuthHeaders() }),
        axios.get(`${API}/prodotti-pulizia?locale_id=${localeCorrente.id}`, { headers: getAuthHeaders() }).catch(() => ({ data: [] }))
      ]);
      setDocumenti(docRes.data);
      setLocali(localiRes.data);
      setDipendenti(dipRes.data);
      setStats(statsRes.data);
      setProdottiPulizia(prodPulRes.data);
    } catch (error) {
      console.error("Errore fetch:", error);
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, localeCorrente?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filtra documenti per tab e ricerca
  const documentiFiltered = documenti.filter(doc => {
    // Filtra per categoria (tab)
    if (doc.categoria !== activeTab) return false;
    
    // Filtra per locale
    if (activeTab === "locale" && filtroLocale !== "all" && doc.locale_id !== filtroLocale) return false;
    
    // Filtra per dipendente
    if (activeTab === "dipendente" && filtroDipendente !== "all" && doc.dipendente_id !== filtroDipendente) return false;
    
    // Filtra per ricerca
    if (search) {
      const s = search.toLowerCase();
      return doc.nome?.toLowerCase().includes(s) || doc.note?.toLowerCase().includes(s);
    }
    
    return true;
  });

  // Dialog
  const openDialog = (item = null, forceDipId = null) => {
    setEditItem(item);
    setTipologiaDoc("documento");
    setFormBusta({ anno: new Date().getFullYear(), mese: new Date().getMonth() + 1 });
    setFormContratto({ data_inizio: "", data_scadenza: "" });
    if (item) {
      setForm({
        nome: item.nome || "",
        data_documento: item.data_documento || "",
        data_scadenza: item.data_scadenza || "",
        note: item.note || "",
        locale_id: item.locale_id || "",
        dipendente_id: item.dipendente_id || "",
        file: null,
        file_name: ""
      });
    } else {
      const dipId = forceDipId || (activeTab === "dipendente" && filtroDipendente !== "all" ? filtroDipendente : "");
      setForm({
        nome: "",
        data_documento: "",
        data_scadenza: "",
        note: "",
        locale_id: activeTab === "locale" && filtroLocale !== "all" ? filtroLocale : "",
        dipendente_id: dipId,
        file: null,
        file_name: ""
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (tipologiaDoc === "documento") {
      if (!form.nome.trim()) { toast.error("Inserisci il nome del documento"); return; }
      if (activeTab === "locale" && !form.locale_id) { toast.error("Seleziona un locale"); return; }
      if (activeTab === "dipendente" && !form.dipendente_id) { toast.error("Seleziona un dipendente"); return; }

      try {
        let documentoId;
        if (editItem) {
          await axios.put(`${API}/documenti/${editItem.id}`, {
            nome: form.nome, data_documento: form.data_documento || null,
            data_scadenza: form.data_scadenza || null, note: form.note || null
          }, { headers: getAuthHeaders() });
          documentoId = editItem.id;
          toast.success("Documento aggiornato");
        } else {
          const res = await axios.post(`${API}/documenti`, {
            categoria: activeTab, nome: form.nome,
            data_documento: form.data_documento || null, data_scadenza: form.data_scadenza || null,
            note: form.note || null, locale_id: form.locale_id || null, dipendente_id: form.dipendente_id || null
          }, { headers: getAuthHeaders() });
          documentoId = res.data.id;
          toast.success("Documento creato");
        }
        if (form.file && documentoId) {
          setUploading(true);
          try {
            await axios.post(`${API}/documenti/${documentoId}/file`, {
              file_data: form.file, file_name: form.file_name
            }, { headers: getAuthHeaders() });
          } catch { toast.error("Errore nel caricamento del file"); }
          setUploading(false);
        }
        setDialogOpen(false);
        fetchData();
      } catch { toast.error("Errore nel salvataggio"); }

    } else if (tipologiaDoc === "contratto") {
      if (!form.file) { toast.error("Seleziona un file per il contratto"); return; }
      if (!form.dipendente_id) { toast.error("Seleziona un dipendente"); return; }
      try {
        await axios.post(`${API}/contratti`, {
          dipendente_id: form.dipendente_id, nome: form.nome || form.file_name,
          data_inizio: formContratto.data_inizio || null, data_scadenza: formContratto.data_scadenza || null,
          file_data: form.file, file_name: form.file_name
        }, { headers: getAuthHeaders() });
        toast.success("Contratto caricato");
        setDialogOpen(false);
        // Refresh contratti per il dipendente espanso
        if (expandedDip === form.dipendente_id) {
          const cRes = await axios.get(`${API}/contratti?dipendente_id=${form.dipendente_id}`, { headers: getAuthHeaders() });
          setDipContratti(prev => ({ ...prev, [form.dipendente_id]: cRes.data }));
        }
      } catch { toast.error("Errore nel salvataggio del contratto"); }

    } else if (tipologiaDoc === "busta_paga") {
      if (!form.file) { toast.error("Seleziona un file per la busta paga"); return; }
      if (!form.dipendente_id) { toast.error("Seleziona un dipendente"); return; }
      try {
        await axios.post(`${API}/buste-paga`, {
          dipendente_id: form.dipendente_id, anno: formBusta.anno, mese: formBusta.mese,
          file_data: form.file, file_name: form.file_name
        }, { headers: getAuthHeaders() });
        toast.success("Busta paga caricata");
        setDialogOpen(false);
        if (expandedDip === form.dipendente_id) {
          const bRes = await axios.get(`${API}/buste-paga?dipendente_id=${form.dipendente_id}`, { headers: getAuthHeaders() });
          setDipBuste(prev => ({ ...prev, [form.dipendente_id]: bRes.data }));
        }
      } catch { toast.error("Errore nel salvataggio della busta paga"); }
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    try {
      await axios.delete(`${API}/documenti/${itemToDelete.id}`, { headers: getAuthHeaders() });
      toast.success("Documento eliminato");
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Upload file
  const handleFileUpload = async (e, documentoId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File troppo grande (max 10MB)");
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        await axios.post(`${API}/documenti/${documentoId}/file`, {
          file_data: reader.result,
          file_name: file.name
        }, { headers: getAuthHeaders() });
        toast.success("File caricato");
        fetchData();
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setUploading(false);
    }
  };

  const handleFileDelete = async (documentoId) => {
    try {
      await axios.delete(`${API}/documenti/${documentoId}/file`, { headers: getAuthHeaders() });
      toast.success("File rimosso");
      fetchData();
    } catch (error) {
      toast.error("Errore nella rimozione");
    }
  };

  // Check scadenza
  const getScadenzaStatus = (dataScadenza) => {
    if (!dataScadenza) return null;
    const oggi = new Date();
    const scadenza = new Date(dataScadenza);
    const diffDays = Math.ceil((scadenza - oggi) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return { label: "Scaduto", color: "bg-red-500" };
    if (diffDays <= 30) return { label: `Scade tra ${diffDays}g`, color: "bg-orange-500" };
    if (diffDays <= 60) return { label: `Scade tra ${diffDays}g`, color: "bg-yellow-500" };
    return null;
  };

  // Personale: expand/collapse dipendente
  const toggleDipendente = async (dipId) => {
    if (expandedDip === dipId) {
      setExpandedDip(null);
      return;
    }
    setExpandedDip(dipId);
    if (hasPersonale && !dipContratti[dipId]) {
      try {
        const [cRes, bRes] = await Promise.all([
          axios.get(`${API}/contratti?dipendente_id=${dipId}`, { headers: getAuthHeaders() }),
          axios.get(`${API}/buste-paga?dipendente_id=${dipId}`, { headers: getAuthHeaders() })
        ]);
        setDipContratti(prev => ({ ...prev, [dipId]: cRes.data }));
        setDipBuste(prev => ({ ...prev, [dipId]: bRes.data }));
      } catch {
        setDipContratti(prev => ({ ...prev, [dipId]: [] }));
        setDipBuste(prev => ({ ...prev, [dipId]: [] }));
      }
    }
  };

  // Scheda tecnica prodotti pulizia
  const handleSchedaUpload = async (e, prodottoId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File troppo grande (max 10MB)"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await axios.post(`${API}/prodotti-pulizia/${prodottoId}/scheda-tecnica`, {
          file_data: reader.result, file_name: file.name
        }, { headers: getAuthHeaders() });
        toast.success("Scheda tecnica caricata");
        fetchData();
      } catch { toast.error("Errore nel caricamento della scheda tecnica"); }
    };
    reader.readAsDataURL(file);
  };

  const handleSchedaDelete = async (prodottoId) => {
    try {
      await axios.delete(`${API}/prodotti-pulizia/${prodottoId}/scheda-tecnica`, { headers: getAuthHeaders() });
      toast.success("Scheda tecnica rimossa");
      fetchData();
    } catch { toast.error("Errore nella rimozione della scheda tecnica"); }
  };

  // Download/Share helper
  const handleDownloadFile = async (fileUrlPath, fileName) => {
    const { fileUrl } = await import("../lib/fileUrl");
    const url = fileUrl(fileUrlPath);
    if (navigator.share) {
      try {
        const res = await fetch(url);
        const blob = await res.blob();
        const file = new File([blob], fileName, { type: blob.type });
        await navigator.share({ files: [file], title: fileName });
        return;
      } catch {}
    }
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Docs per dipendente
  const getDocsPerDipendente = (dipId) => documenti.filter(d => d.categoria === "dipendente" && d.dipendente_id === dipId);

  // Search filter for personale tab
  const dipendentiFiltrati = dipendenti.filter(d => {
    if (!search) return true;
    const s = search.toLowerCase();
    const nome = (d.nome || d.email || d.telefono || "").toLowerCase();
    return nome.includes(s);
  });

  const TabIcon = { societa: Building2, locale: Store, dipendente: Users }[activeTab];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <FileText className="h-7 w-7 text-primary" />
            Archivio documenti
          </h1>
          <p className="text-muted-foreground mt-1">
            {stats.totale || 0} documenti totali
            {stats.in_scadenza_30g > 0 && (
              <span className="text-orange-500 ml-2">• {stats.in_scadenza_30g} in scadenza</span>
            )}
            {stats.scaduti > 0 && (
              <span className="text-red-500 ml-2">• {stats.scaduti} scaduti</span>
            )}
          </p>
        </div>
        <Button onClick={() => openDialog()} className="gap-2">
          <Plus className="h-4 w-4" />
          Nuovo Documento
        </Button>
      </div>

      {/* Alert scadenze */}
      {(stats.scaduti > 0 || stats.in_scadenza_30g > 0) && (
        <Card className="bg-orange-500/10 border-orange-500/30">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-orange-500 shrink-0 mt-0.5" />
              <div className="text-sm space-y-1 flex-1">
                {stats.scaduti > 0 && (
                  <div>
                    <span className="text-red-500 font-medium">{stats.scaduti} document{stats.scaduti === 1 ? "o" : "i"} scadut{stats.scaduti === 1 ? "o" : "i"}</span>
                    {stats.scaduti_dettaglio?.length > 0 && (
                      <div className="mt-1.5 space-y-1">
                        {stats.scaduti_dettaglio.map((d, i) => (
                          <button
                            key={d.id || i}
                            onClick={() => {
                              if (d.categoria) setActiveTab(d.categoria);
                              if (d.categoria === "dipendente" && d.dipendente_id) setExpandedDip(d.dipendente_id);
                              setTimeout(() => {
                                const el = document.querySelector(`[data-doc-id="${d.id}"]`);
                                if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("ring-2", "ring-red-400"); setTimeout(() => el.classList.remove("ring-2", "ring-red-400"), 3000); }
                              }, 500);
                            }}
                            className="flex items-center gap-1.5 text-xs text-red-500 font-medium hover:text-red-700 hover:underline transition-colors cursor-pointer w-full text-left"
                            data-testid={`scaduto-link-${d.id}`}
                          >
                            <span>•</span>
                            <span>{d.nome}</span>
                            {d.data_scadenza && <span className="text-muted-foreground font-normal">(scaduto il {new Date(d.data_scadenza).toLocaleDateString("it-IT")})</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {stats.scaduti > 0 && stats.in_scadenza_30g > 0 && <div className="border-t border-orange-200 my-1" />}
                {stats.in_scadenza_30g > 0 && <span className="text-orange-500">{stats.in_scadenza_30g} in scadenza nei prossimi 30 giorni</span>}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex h-auto w-full overflow-x-auto">
          <TabsTrigger value="societa" className="gap-1 px-2 flex-1">
            <Building2 className="h-4 w-4 shrink-0" />
            <span className="hidden sm:inline text-xs">Società</span>
            <Badge variant="secondary" className="ml-0.5 text-[10px]">{stats.totale_societa || 0}</Badge>
          </TabsTrigger>
          <TabsTrigger value="locale" className="gap-1 px-2 flex-1">
            <Store className="h-4 w-4 shrink-0" />
            <span className="hidden sm:inline text-xs">Locali</span>
            <Badge variant="secondary" className="ml-0.5 text-[10px]">{stats.totale_locale || 0}</Badge>
          </TabsTrigger>
          <TabsTrigger value="dipendente" className="gap-1 px-2 flex-1">
            <Users className="h-4 w-4 shrink-0" />
            <span className="hidden sm:inline text-xs">Personale</span>
            <Badge variant="secondary" className="ml-0.5 text-[10px]">{stats.totale_dipendente || 0}</Badge>
          </TabsTrigger>
          <TabsTrigger value="prodotti_pulizia" className="gap-1 px-2 flex-1 whitespace-nowrap">
            <SprayCan className="h-4 w-4 shrink-0" />
            <span className="hidden sm:inline text-xs">Prodotti per la pulizia</span>
            <Badge variant="secondary" className="ml-0.5 text-[10px]">{prodottiPulizia.length}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* Filtri e ricerca */}
        <div className="flex flex-col sm:flex-row gap-3 mt-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Cerca documento..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          
          {activeTab === "locale" && locali.length > 1 && (
            <Select value={filtroLocale} onValueChange={setFiltroLocale}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Tutti i locali" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i locali</SelectItem>
                {locali.map(l => (
                  <SelectItem key={l.id} value={l.id}>{l.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          {activeTab === "dipendente" && (
            <Select value={filtroDipendente} onValueChange={setFiltroDipendente}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Tutti" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {dipendenti.map(d => (
                  <SelectItem key={d.id} value={d.id}>{d.nome} {d.cognome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Lista documenti - Società e Locale */}
        {(activeTab === "societa" || activeTab === "locale") && (
          <TabsContent value={activeTab} className="mt-4">
            {documentiFiltered.length === 0 ? (
              <Card className="bg-muted/30">
                <CardContent className="p-8 text-center">
                  <TabIcon className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
                  <p className="text-muted-foreground">Nessun documento</p>
                  <Button onClick={() => openDialog()} variant="outline" className="mt-4 gap-2">
                    <Plus className="h-4 w-4" />
                    Aggiungi documento
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {documentiFiltered.map(doc => {
                  const scadenza = getScadenzaStatus(doc.data_scadenza);
                  return (
                    <Card key={doc.id} className="hover:bg-muted/30 transition-colors" data-testid={`archivio-doc-card-${doc.id}`} data-doc-id={doc.id}>
                      <CardContent className="p-3 sm:p-4">
                        <div className="flex items-center gap-3">
                          {doc.file_url ? (
                            <button
                              type="button"
                              onClick={() => handleDownloadFile(doc.file_url, doc.nome + (doc.file_url.substring(doc.file_url.lastIndexOf('.')) || ''))}
                              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-primary text-white hover:bg-primary/90 transition-colors active:scale-95 shadow-sm"
                              title="Scarica / Condividi"
                              data-testid={`download-doc-${doc.id}`}
                            >
                              <Download className="h-5 w-5" />
                            </button>
                          ) : (
                            <label className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-muted hover:bg-muted/80 cursor-pointer text-muted-foreground hover:text-primary transition-colors" title="Carica file">
                              <input type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,image/*" onChange={(e) => handleFileUpload(e, doc.id)} disabled={uploading} data-testid={`upload-doc-${doc.id}`} />
                              <Upload className="h-5 w-5" />
                            </label>
                          )}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-foreground text-sm truncate">{doc.nome}</h3>
                            <div className="flex flex-wrap items-center gap-1.5 mt-0.5 text-xs text-muted-foreground">
                              {doc.data_documento && (<span>{new Date(doc.data_documento).toLocaleDateString("it-IT")}</span>)}
                              {doc.data_scadenza && (<span className={scadenza ? "text-red-400" : ""}>Scade: {new Date(doc.data_scadenza).toLocaleDateString("it-IT")}</span>)}
                              {activeTab === "locale" && doc.locale_nome && (<Badge variant="outline" className="text-[10px] px-1.5 py-0">{doc.locale_nome}</Badge>)}
                              {scadenza && (<Badge className={`${scadenza.color} text-white text-[10px] px-1.5 py-0`}>{scadenza.label}</Badge>)}
                            </div>
                          </div>
                          <div className="flex items-center gap-0.5 shrink-0">
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" onClick={() => openDialog(doc)} data-testid={`edit-doc-${doc.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-400 hover:bg-red-500/10" onClick={() => { setItemToDelete(doc); setDeleteDialogOpen(true); }} data-testid={`delete-doc-${doc.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        )}

        {/* Vista Personale - raggruppata per dipendente */}
        {activeTab === "dipendente" && (
          <TabsContent value="dipendente" className="mt-4">
            {dipendentiFiltrati.length === 0 ? (
              <Card className="bg-muted/30">
                <CardContent className="p-8 text-center">
                  <Users className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
                  <p className="text-muted-foreground">Nessun dipendente trovato</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {dipendentiFiltrati.filter(d => filtroDipendente === "all" || d.id === filtroDipendente).map(dip => {
                  const isExpanded = expandedDip === dip.id;
                  const dipDocs = getDocsPerDipendente(dip.id);
                  const contratti = dipContratti[dip.id] || [];
                  const buste = dipBuste[dip.id] || [];
                  const totalDocs = dipDocs.length + (hasPersonale ? contratti.length + buste.length : 0);

                  return (
                    <Card key={dip.id} className="overflow-hidden" data-testid={`personale-card-${dip.id}`}>
                      {/* Header dipendente - cliccabile */}
                      <button
                        className="w-full flex items-center gap-3 p-3 sm:p-4 hover:bg-muted/30 transition-colors active:scale-[0.99] text-left"
                        onClick={() => toggleDipendente(dip.id)}
                        data-testid={`toggle-dip-${dip.id}`}
                      >
                        <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                          <Users className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-foreground text-sm truncate">{dip.nome || dip.email || dip.telefono}</p>
                          <p className="text-xs text-muted-foreground">{totalDocs} documenti</p>
                        </div>
                        {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
                      </button>

                      {/* Contenuto espanso */}
                      {isExpanded && (
                        <div className="border-t border-border">
                          {/* === DOCUMENTI === */}
                          <div className="p-3 sm:p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                                <FolderOpen className="h-3.5 w-3.5" /> Documenti
                              </h4>
                              <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-primary" onClick={() => openDialog(null, dip.id)} data-testid={`add-doc-dip-${dip.id}`}>
                                <Plus className="h-3 w-3" /> Aggiungi
                              </Button>
                            </div>
                            {dipDocs.length === 0 ? (
                              <p className="text-xs text-muted-foreground py-2">Nessun documento</p>
                            ) : (
                              <div className="space-y-1.5">
                                {dipDocs.map(doc => {
                                  const scadenza = getScadenzaStatus(doc.data_scadenza);
                                  return (
                                    <div key={doc.id} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-muted/30" data-testid={`dip-doc-${doc.id}`} data-doc-id={doc.id}>
                                      {doc.file_url ? (
                                        <button onClick={() => handleDownloadFile(doc.file_url, doc.nome + (doc.file_url.substring(doc.file_url.lastIndexOf('.')) || ''))} className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-primary text-white hover:bg-primary/90 active:scale-95 shadow-sm" data-testid={`dl-dip-doc-${doc.id}`}>
                                          <Download className="h-4 w-4" />
                                        </button>
                                      ) : (
                                        <label className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-muted hover:bg-muted/80 cursor-pointer text-muted-foreground">
                                          <input type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,image/*" onChange={(e) => handleFileUpload(e, doc.id)} />
                                          <Upload className="h-4 w-4" />
                                        </label>
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium truncate">{doc.nome}</p>
                                        <div className="flex items-center gap-1.5 mt-0.5">
                                          {doc.data_scadenza && <span className={`text-[10px] ${scadenza ? "text-red-400" : "text-muted-foreground"}`}>Scade: {new Date(doc.data_scadenza).toLocaleDateString("it-IT")}</span>}
                                          {scadenza && <Badge className={`${scadenza.color} text-white text-[9px] px-1 py-0`}>{scadenza.label}</Badge>}
                                        </div>
                                      </div>
                                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={() => openDialog(doc)}><Pencil className="h-3 w-3" /></Button>
                                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => { setItemToDelete(doc); setDeleteDialogOpen(true); }}><Trash2 className="h-3 w-3" /></Button>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {/* === CONTRATTI (solo con pacchetto Personale) === */}
                          {hasPersonale && (
                            <div className="p-3 sm:p-4 border-t border-border/60">
                              <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5 mb-2">
                                <Briefcase className="h-3.5 w-3.5" /> Contratti
                              </h4>
                              {contratti.length === 0 ? (
                                <p className="text-xs text-muted-foreground py-2">Nessun contratto allegato</p>
                              ) : (
                                <div className="space-y-1.5">
                                  {contratti.map(c => (
                                    <div key={c.id} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-muted/30" data-testid={`contratto-row-${c.id}`}>
                                      {c.file_url ? (
                                        <button onClick={() => handleDownloadFile(c.file_url, c.file_nome || c.nome)} className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-primary text-white hover:bg-primary/90 active:scale-95 shadow-sm">
                                          <Download className="h-4 w-4" />
                                        </button>
                                      ) : (
                                        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-muted"><Briefcase className="h-4 w-4 text-muted-foreground" /></div>
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium truncate">{c.nome || c.file_nome}</p>
                                        {(c.data_inizio || c.data_scadenza) && (
                                          <p className="text-[10px] text-muted-foreground">
                                            {c.data_inizio && new Date(c.data_inizio).toLocaleDateString("it-IT")}
                                            {c.data_inizio && c.data_scadenza && " → "}
                                            {c.data_scadenza && new Date(c.data_scadenza).toLocaleDateString("it-IT")}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {/* === BUSTE PAGA (solo con pacchetto Personale) === */}
                          {hasPersonale && (
                            <div className="p-3 sm:p-4 border-t border-border/60">
                              <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5 mb-2">
                                <Receipt className="h-3.5 w-3.5" /> Buste Paga
                              </h4>
                              {buste.length === 0 ? (
                                <p className="text-xs text-muted-foreground py-2">Nessuna busta paga</p>
                              ) : (
                                <div className="space-y-1.5">
                                  {buste.map(b => (
                                    <div key={b.id} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-muted/30" data-testid={`busta-row-${b.id}`}>
                                      <button onClick={() => handleDownloadFile(b.file_url, b.file_nome || `Busta_${b.mese_label}_${b.anno}.pdf`)} className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-primary text-white hover:bg-primary/90 active:scale-95 shadow-sm">
                                        <Download className="h-4 w-4" />
                                      </button>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium truncate">{b.mese_label} {b.anno}</p>
                                        <p className="text-[10px] text-muted-foreground">{b.file_nome}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        )}

        {/* Vista Prodotti Pulizia - schede tecniche */}
        {activeTab === "prodotti_pulizia" && (
          <TabsContent value="prodotti_pulizia" className="mt-4">
            {prodottiPulizia.length === 0 ? (
              <Card className="bg-muted/30">
                <CardContent className="p-8 text-center">
                  <SprayCan className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
                  <p className="text-muted-foreground">Nessun prodotto configurato</p>
                  <p className="text-xs text-muted-foreground mt-1">Crea i prodotti dalla sezione HACCP → Piano Pulizie → Prodotti</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {prodottiPulizia.filter(p => !search || p.nome?.toLowerCase().includes(search.toLowerCase())).map(p => (
                  <Card key={p.id} className="hover:bg-muted/30 transition-colors" data-testid={`prodotto-pulizia-${p.id}`}>
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex items-center gap-3">
                        {/* Pulsante download se ha scheda, upload se non ce l'ha */}
                        {p.scheda_tecnica_url ? (
                          <button
                            onClick={() => handleDownloadFile(p.scheda_tecnica_url, p.scheda_tecnica_nome || `Scheda_${p.nome}.pdf`)}
                            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-primary text-white hover:bg-primary/90 transition-colors active:scale-95 shadow-sm"
                            title="Scarica Scheda Tecnica"
                            data-testid={`download-scheda-${p.id}`}
                          >
                            <Download className="h-5 w-5" />
                          </button>
                        ) : (
                          <label
                            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-muted hover:bg-muted/80 cursor-pointer text-muted-foreground hover:text-primary transition-colors"
                            title="Carica Scheda Tecnica"
                            data-testid={`upload-scheda-label-${p.id}`}
                          >
                            <input
                              type="file"
                              className="hidden"
                              accept=".pdf,image/*"
                              onChange={(e) => handleSchedaUpload(e, p.id)}
                              data-testid={`upload-scheda-${p.id}`}
                            />
                            <Upload className="h-5 w-5" />
                          </label>
                        )}

                        {/* Info prodotto */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-foreground text-sm truncate">{p.nome}</h3>
                          <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 capitalize">{p.tipo || 'prodotto'}</Badge>
                            {p.scheda_tecnica_url ? (
                              <span className="text-[10px] text-emerald-500 font-medium">Scheda allegata</span>
                            ) : (
                              <span className="text-[10px] text-muted-foreground">Nessuna scheda tecnica</span>
                            )}
                          </div>
                          {p.note && <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{p.note}</p>}
                        </div>

                        {/* Pulsante elimina scheda (solo se esiste) */}
                        {p.scheda_tecnica_url && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-400 hover:bg-red-500/10 shrink-0"
                            onClick={() => handleSchedaDelete(p.id)}
                            title="Rimuovi scheda tecnica"
                            data-testid={`delete-scheda-${p.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Dialog Documento */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TabIcon className="h-5 w-5" />
              {editItem ? "Modifica Documento" : "Nuovo Documento"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Tipologia (solo in creazione da tab Personale) */}
            {activeTab === "dipendente" && !editItem && (
              <div>
                <Label>Tipologia *</Label>
                <div className="grid grid-cols-3 gap-2 mt-1.5">
                  {[
                    { value: "documento", label: "Documento", icon: FolderOpen },
                    ...(hasPersonale ? [
                      { value: "contratto", label: "Contratto", icon: Briefcase },
                      { value: "busta_paga", label: "Busta Paga", icon: Receipt }
                    ] : [])
                  ].map(t => (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => setTipologiaDoc(t.value)}
                      className={cn(
                        "flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 text-xs font-bold transition-all active:scale-95",
                        tipologiaDoc === t.value
                          ? "border-primary bg-primary text-white shadow-sm"
                          : "border-border bg-card text-muted-foreground hover:border-primary/30"
                      )}
                      data-testid={`tipo-doc-${t.value}`}
                    >
                      <t.icon className="h-5 w-5" />
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Nome */}
            {tipologiaDoc === "documento" && (
              <div>
                <Label>Nome Documento *</Label>
                <Input
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  placeholder="es. Attestato HACCP, Certificato..."
                />
              </div>
            )}
            {tipologiaDoc === "contratto" && (
              <div>
                <Label>Nome Contratto</Label>
                <Input
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  placeholder="es. Contratto TD, Rinnovo..."
                />
              </div>
            )}
            
            {/* Locale (solo tab locale) */}
            {activeTab === "locale" && !editItem && (
              <div>
                <Label>Locale *</Label>
                <Select value={form.locale_id} onValueChange={(v) => setForm({ ...form, locale_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Seleziona locale" /></SelectTrigger>
                  <SelectContent>
                    {locali.map(l => (<SelectItem key={l.id} value={l.id}>{l.nome}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Dipendente (solo tab personale in creazione) */}
            {activeTab === "dipendente" && !editItem && (
              <div>
                <Label>Dipendente *</Label>
                <SearchableSelect
                  items={dipendenti}
                  value={form.dipendente_id}
                  onChange={(id) => setForm({ ...form, dipendente_id: id })}
                  placeholder="Seleziona dipendente..."
                  searchPlaceholder="Cerca dipendente..."
                  icon={Users}
                  getItemLabel={(d) => d.nome || d.email || d.telefono}
                  emptyMessage="Nessun dipendente trovato"
                />
              </div>
            )}

            {/* Campi specifici per Contratto */}
            {tipologiaDoc === "contratto" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Data Inizio</Label>
                  <DateInput value={formContratto.data_inizio} onChange={(v) => setFormContratto({ ...formContratto, data_inizio: v })} />
                </div>
                <div>
                  <Label>Scadenza</Label>
                  <DateInput value={formContratto.data_scadenza} onChange={(v) => setFormContratto({ ...formContratto, data_scadenza: v })} />
                </div>
              </div>
            )}

            {/* Campi specifici per Busta Paga */}
            {tipologiaDoc === "busta_paga" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Anno</Label>
                  <Input type="number" min="2020" max="2030" value={formBusta.anno} onChange={(e) => setFormBusta({ ...formBusta, anno: parseInt(e.target.value) || 2026 })} />
                </div>
                <div>
                  <Label>Mese</Label>
                  <Select value={String(formBusta.mese)} onValueChange={(v) => setFormBusta({ ...formBusta, mese: parseInt(v) })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"].map((m, i) => (
                        <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Date documento (solo per tipologia documento) */}
            {tipologiaDoc === "documento" && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Data Documento</Label>
                    <DateInput value={form.data_documento} onChange={(v) => setForm({ ...form, data_documento: v })} />
                  </div>
                  <div>
                    <Label>Data Scadenza</Label>
                    <DateInput value={form.data_scadenza} onChange={(v) => setForm({ ...form, data_scadenza: v })} />
                  </div>
                </div>
                <div>
                  <Label>Note</Label>
                  <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Note opzionali..." rows={2} />
                </div>
              </>
            )}
            
            {/* Upload Allegato */}
            <div>
              <Label>{tipologiaDoc === "documento" ? "Allegato (PDF, immagine, documento)" : "File *"}</Label>
              <div className="mt-2">
                {form.file_name ? (
                  <div className="flex items-center gap-2 p-3 bg-primary/10 rounded-lg border border-primary/20">
                    <FileUp className="h-5 w-5 text-primary" />
                    <span className="flex-1 text-sm font-medium truncate">{form.file_name}</span>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => setForm({ ...form, file: null, file_name: "" })}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-muted-foreground/25 rounded-lg cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors">
                    <div className="flex flex-col items-center justify-center pt-2 pb-3">
                      <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">Clicca per caricare un file</p>
                      <p className="text-xs text-muted-foreground/70">Max 10MB</p>
                    </div>
                    <input type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif" onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (file.size > 10 * 1024 * 1024) { toast.error("File troppo grande (max 10MB)"); return; }
                        const reader = new FileReader();
                        reader.onload = () => { setForm({ ...form, file: reader.result, file_name: file.name }); };
                        reader.readAsDataURL(file);
                      }
                    }} />
                  </label>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleSubmit} disabled={uploading}>
              {uploading ? "Caricamento..." : (editItem ? "Salva" : "Crea")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Conferma Eliminazione */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma eliminazione</DialogTitle>
          </DialogHeader>
          <p className="py-4 text-muted-foreground">
            Sei sicuro di voler eliminare "{itemToDelete?.nome}"?
            {itemToDelete?.file_url && " Il file allegato verrà eliminato."}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Annulla</Button>
            <Button variant="destructive" onClick={handleDelete}>Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArchivioDocumenti;
